import {
  Box, Grid, Link,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { v4 as uuid } from 'uuid';
import EditableText from '../../Form/EditableText';
import CloseButton from '../../Buttons/Close';
import Utils from '../../../utils/Utils';
import BlockProps from '../../../models/BlockProps';
import BlockImageAndTextData from './BlockImageAndTextData';
import ImageSettingsModal from '../../Modals/ImageSettingsModal/ImageSettingsModal';
import CroppedImage from '../../CroppedImage';
import BlockComponentImage from '../../../models/BlockComponentValues/BlockComponentImage';
import BlockData from '../../../models/BlockData';
import useGetBackground from '../../../hooks/UseGetBackground';
import BackGroundImageModal from '../../Modals/ImageModal/BackGroundImageModal';
import useParallaxScript from '../../../hooks/useParallaxScript';
import config from '../../../config';
import ImageEnlargeModal from '../../Images/ImageEnlargeModal';

export interface BlockImageAndTextProps extends BlockProps {
  blockData: BlockData[]
}

function BlockImageAndText({
  data,
  isEditing,
  isTemplate,
  editHandler,
  showImageModal,
  onCloseImageModal,
  blockData,
}: BlockImageAndTextProps) {
  const targetModalId = uuid();

  const [props, setProps] = useState<BlockImageAndTextData>(data.data as BlockImageAndTextData);

  useEffect(() => {
    setProps(data.data as BlockImageAndTextData);
  }, [data.data]);

  const handleTitleUpdate = (text?: string) => {
    props.title.value = text;
    editHandler(data.id, props);
  };

  const handleDescriptionUpdate = (text?: string) => {
    props.description.value = text;
    editHandler(data.id, props);
  };

  const deleteImage = () => {
    props.mainImage = undefined;
    editHandler(data.id, props);
  };

  const elementUuid = uuid();
  useParallaxScript(props.backgroundImage?.settings?.parallaxBool, elementUuid, config.backgroundImage.parallaxEffect);

  return (
    <>
      <Grid
        id={elementUuid}
        container
        spacing={0}
        direction="column"
        alignItems="center"
        justifyContent="center"
        sx={{
          minHeight: '250px',
          overflow: 'hidden',
          ...useGetBackground(
            props.backgroundImage?.solidColor,
            props.backgroundImage?.value,
            props.backgroundImage?.settings?.scale,
            props.backgroundImage?.settings?.scaleBool,
          ),
          backgroundPosition: 'center 0px',
        }}
      >
        {(isEditing && !isTemplate) && (
          <BackGroundImageModal
            backgroundImage={props.backgroundImage}
            isShowModal={showImageModal}
            onCloseModal={onCloseImageModal}
            handleOnConfirm={(url: BlockComponentImage) => {
              const tempBg = { ...props, backgroundImage: { ...url } };
              setProps(tempBg);
              editHandler(data.id, tempBg);
            }}
          />
        )}

        <Grid
          item
          xs={12}
          sx={{
            marginTop: 8,
          }}
        />

        <Grid
          container
          spacing={0}
          direction="column"
          alignItems="center"
          justifyContent="center"
          className="block-content-wrapper"
        >
          {props.title && (
            <Grid
              item
              xs={12}
              sx={{
                position: 'relative',
                mx: 2,
              }}
            >
              <Box sx={Utils.hoverBorder(isEditing)}>
                {isEditing && (
                  <CloseButton updateHandler={() => {
                    props.title = undefined;
                    editHandler(data.id, props);
                  }}
                  />
                )}
                <EditableText
                  text={props.title.value}
                  textColor={props.title.settings?.textColor}
                  isEditable={isEditing && !isTemplate}
                  updateHandler={handleTitleUpdate}
                />
              </Box>
            </Grid>
          )}

          <Grid
            container
            spacing={0}
            direction="row"
            alignItems="stretch"
            justifyContent="space-between"
          >
            <Grid
              item
              xs={4}
              sx={{
                position: 'relative',
                my: 5,
                paddingLeft: 8,
              }}
            >
              <Grid
                container
                spacing={0}
                direction="column"
                alignItems="center"
                justifyContent="center"
                sx={{
                  height: 1,
                }}
              >

                {props.mainImage && (
                  <Box sx={Utils.hoverBorder(isEditing)} style={{ width: '100%', height: 'auto', display: 'flex' }}>

                    {isEditing && <CloseButton updateHandler={() => { deleteImage(); }} />}

                    <label htmlFor={`${data.id}_main`} style={{ width: '100%', height: 'auto' }}>
                      <ImageSettingsModal
                        isEditing={isEditing}
                        blockData={blockData}
                        image={props.mainImage}
                        disabled={isTemplate || !isEditing}
                        onConfirm={(newImage: BlockComponentImage) => {
                          props.mainImage = newImage;
                          editHandler(data.id, props);
                        }}
                      >
                        {(!isEditing) ? (
                          <Link
                            style={{ cursor: props?.mainImage?.link?.linkData?.url ? 'pointer' : 'default' }}
                            target={props?.mainImage?.link?.linkData?.openType}
                            href={props?.mainImage?.link?.linkData?.url || undefined}
                            sx={{
                              width: '100%',
                              height: '100%',
                            }}
                            rel="noreferrer"
                          >
                            <CroppedImage
                              image={props.mainImage}
                              isEditing={isEditing}
                              settings={props.mainImage.settings}
                              targetModalId={targetModalId}
                            />
                          </Link>
                        ) : (
                          <CroppedImage
                            image={props.mainImage}
                            isEditing={isEditing}
                            settings={props.mainImage.settings}
                            targetModalId={targetModalId}
                          />
                        )}
                      </ImageSettingsModal>
                    </label>
                  </Box>
                )}

              </Grid>
            </Grid>

            {props.description && (
              <Grid
                item
                xs={7}
                sx={Utils.hoverBorder(isEditing, {
                  my: 5, mx: 2,
                })}
              >

                {isEditing && (
                  <CloseButton updateHandler={() => {
                    props.description = undefined;
                    editHandler(data.id, props);
                  }}
                  />
                )}

                <EditableText
                  text={props.description.value}
                  textColor={props.description.settings?.textColor}
                  isEditable={isEditing && !isTemplate}
                  updateHandler={handleDescriptionUpdate}
                />

              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>

      <ImageEnlargeModal
        images={[props.mainImage]}
        targetModalId={targetModalId}
      />
    </>
  );
}

export default BlockImageAndText;
